import React, { useMemo, useState } from 'react';
import { Edit2 } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import TaskModal from './TaskModal';

const QUADRANTS = [
  { imp: 'Hoch', urg: 'Sofort', label: 'Tun', sub: 'Wichtig & Dringend', color: 'var(--red)', emoji: '🔥' },
  { imp: 'Hoch', urg: 'Diese Woche', label: 'Planen', sub: 'Wichtig, nicht dringend', color: 'var(--accent)', emoji: '📅' },
  { imp: 'Niedrig', urg: 'Sofort', label: 'Delegieren', sub: 'Dringend, nicht wichtig', color: 'var(--blue)', emoji: '🤝' },
  { imp: 'Niedrig', urg: 'Irgendwann', label: 'Eliminieren', sub: 'Nicht wichtig & nicht dringend', color: 'var(--text-3)', emoji: '🗑' },
];

// Also map "Mittel" importance
function getQuadrant(task) {
  const isHigh = ['Hoch', 'Mittel'].includes(task.importance);
  const isUrgent = task.urgency === 'Sofort';
  const isSoon = task.urgency === 'Diese Woche';

  if (isHigh && isUrgent) return 0;
  if (isHigh && (isSoon || !isUrgent)) return 1;
  if (!isHigh && isUrgent) return 2;
  return 3;
}

export default function EisenhowerMatrix() {
  const { tasks, refreshTasks, isDone } = useApp();
  const [editTask, setEditTask] = useState(null);

  const activeTasks = tasks.filter(t => !isDone(t));

  const byQuadrant = useMemo(() => {
    const q = [[], [], [], []];
    activeTasks.forEach(t => q[getQuadrant(t)].push(t));
    return q;
  }, [activeTasks]);

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 28 }}>Eisenhower Matrix</h1>
        <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 4 }}>Nur aktive Tasks (ohne Done / Abgebrochen)</p>
      </div>

      {/* Axis labels */}
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: 1, textTransform: 'uppercase' }}>← Weniger wichtig — Wichtiger →</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {QUADRANTS.map((q, i) => (
            <div key={i} style={{
              background: 'var(--bg-1)', border: `1px solid var(--border)`,
              borderTop: `3px solid ${q.color}`,
              borderRadius: 'var(--radius-lg)', padding: 16,
              minHeight: 200,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 20 }}>{q.emoji}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: q.color }}>{q.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{q.sub}</div>
                </div>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)' }}>{byQuadrant[i].length}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {byQuadrant[i].length === 0 && (
                  <div style={{ color: 'var(--text-3)', fontSize: 12, fontStyle: 'italic' }}>Keine Tasks</div>
                )}
                {byQuadrant[i].map(task => (
                  <div key={task.id} style={{
                    background: 'var(--bg-2)', borderRadius: 'var(--radius)',
                    padding: '8px 10px', display: 'flex', alignItems: 'flex-start', gap: 8,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{task.title}</div>
                      {task.owner && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>👤 {task.owner}</div>}
                    </div>
                    <button className="btn btn-icon btn-ghost btn-sm" onClick={() => setEditTask(task)}>
                      <Edit2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Urgency label on side */}
        <div style={{
          position: 'absolute', left: -20, top: '50%', transform: 'translateY(-50%) rotate(-90deg)',
          fontSize: 11, color: 'var(--text-3)', letterSpacing: 1, textTransform: 'uppercase', whiteSpace: 'nowrap',
        }}>
          ← Weniger dringend — Dringender →
        </div>
      </div>

      {editTask && <TaskModal task={editTask} onClose={() => setEditTask(null)} onSaved={refreshTasks} />}
    </div>
  );
}
